import React from 'react'
import { captureException } from '@sentry/react'

import { YStack } from 'tamagui'

import { Icon } from './Icon'
import { H3, Text } from './Text'
import { ExternalLink } from './Link'

type ErrorBoundaryProps = {
  children: React.ReactNode
  fallback?: React.ReactNode
  onError?: (error: Error) => void
}

const DefaultErrorFallback = () => (
  <YStack flexGrow={1} alignItems='center' justifyContent='center' padding='$6'>
    <YStack alignItems='center' gap='$4' padding='$6' maxWidth={480}>
      <Icon name='error-circle' size='$9' color='$red9' />

      <H3 size='$7' textAlign='center'>
        Something went wrong
      </H3>

      <Text size='$4' color='$color' textAlign='center'>
        Please try refreshing the page, or email{' '}
        <span className='notranslate'>
          <ExternalLink href='mailto:hi@guild.host'>
            <Text
              size='$4'
              color='$primaryColor'
              textDecorationLine='underline'
            >
              hi@guild.host
            </Text>
          </ExternalLink>
        </span>{' '}
        for help.
      </Text>
    </YStack>
  </YStack>
)

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  { hasError: boolean }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  componentDidCatch(error: Error) {
    console.error('ERROR', error)

    captureException(error)
    this.props.onError?.(error)
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultErrorFallback />
    }
    return this.props.children
  }
}
